import { isEmptyObject } from 'es-toolkit';
import { api } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { loadNewAuth, unauthenticateUser } from 'src/common/actions/auth';
import { FETCH_CONFIG } from 'src/common/actions/config';
import { FETCH_ME } from 'src/common/actions/me';
import { config as partnersConfig } from 'src/common/constants/partners/alexa';
import { fulfilled } from 'src/common/reducers/utils/asyncActionNameSuffixes';
import { selectUserIpAddress } from 'src/common/selectors/app';
import { generateRoutePathForGuideItem } from 'src/common/utils/generateRoutePathForGuideItem';
import getConfigStreamFormats from 'src/common/utils/getConfigStreamFormats';
import { configApiOptions } from '../../common/config/configApiOptions';
import { getBaseApiConfig } from '../../common/config/utils';
import { getGuildIdQueryParam } from '../../common/utils/discord';
import { isDevBuild } from '../../common/utils/environment';

export default function createTuneInApiClient(initialState = {}) {
  const { auth, config, app = {}, me } = initialState;
  const authTokens = isEmptyObject(auth?.tokenInfo ?? {}) ? null : auth.tokenInfo;
  const {
    isMobile,
    isDesktop,
    isDiscord,
    desktopVersion,
    tuneInUserSerial,
    partnerId,
    userAgent,
    isBot,
  } = app;

  api.init({
    ...getBaseApiConfig(isDiscord),
    configServiceOptions: configApiOptions,
    partnersServiceOptions: partnersConfig,
    uiServiceOptions: {
      generateRoutePathForGuideItem: generateRoutePathForGuideItem((...args) =>
        api.telemetry.logError(...args),
      ),
    },
    configData: config,
    userData: me?.details || null,
    partnerId,
    serial: tuneInUserSerial,
    authTokens,
    optionalParams: {
      formats: getConfigStreamFormats(isMobile, isDesktop, isDiscord, userAgent),
      desktopVersion,
      ...(isDiscord && { productMode: getGuildIdQueryParam() }),
    },
    debug: isDevBuild(),
    ipAddress: selectUserIpAddress(initialState),
    // Crawlers execute our client bundle, so every page they render emits a screen view. They all
    // share one serial (see `tuneInUserSerialMiddleware`), so in the data they look like a single,
    // implausibly active device, and their volume follows crawl scheduling rather than anything a
    // user did. Suppress the reports rather than filtering downstream, so every consumer of the
    // events gets the same answer.
    enableScreenViewReporting: !isBot,
  });

  api.on('authTokensUpdated', (authTokens) => {
    if (authTokens?.accessToken) {
      storeSingleton.dispatch(loadNewAuth(authTokens));
    } else {
      storeSingleton.dispatch(unauthenticateUser());
    }
  });

  api.on('configData', (configData) => {
    storeSingleton.dispatch({
      type: fulfilled(FETCH_CONFIG),
      payload: { ...configData, loadedTime: Date.now() },
    });
  });

  // Clearing of user data is handled indirectly by the unauthenticateUser action above in the authTokensUpdated listener
  api.on('userData', (userData) => {
    if (userData) {
      storeSingleton.dispatch({
        type: fulfilled(FETCH_ME),
        payload: {
          content: userData,
          loadedTime: Date.now(),
        },
        meta: {
          contentId: 'details',
        },
      });
    }
  });
}
